import {
  clone,
  defaults,
  eq,
  filter,
  find,
  findIndex,
  forEach,
  get,
  has,
  includes,
  isEmpty,
  isNull,
  isArray,
  isObject,
  isUndefined,
  join,
  keys,
  last,
  map,
  reduce,
  replace,
  split
} from 'lodash-es'
import commonLocaleBundle from './'
import ConfigurationService from '../config/configService'

/**
 * Configuration namespace for this module
 * @type {string}
 */
const moduleConfNamespace = 'locale'

/**
 * Locale bundles
 * Sorted by ascending order of precedence
 */
const loadedLocaleBundles = [
  { common: commonLocaleBundle },
  { commonServer: window?.advAppData?.locales?.common || {} },
  { client: false },
  { clientServer: window?.advAppData?.locales?.client || {} }
]

/**
 * Other dependencies used by this service
 * @type {{conf: {get: (function(*=, *=): *), replaceLoadedBundle: ConfigService.replaceLoadedBundle, getLoadedBundles: (function(): [{common: {}}, {client: boolean}])}}}
 */
const dependencies = {
  conf: ConfigurationService
}

/**
 * Gets the requested localization item (by its key & namespace)
 * @param key {string}
 * @param params {object}
 * @param namespace {string}
 * @param linePrefix {string}
 * @param lineSuffix {string}
 * @param targetLocale {string|false}
 * @returns {string|false}
 */
const getTranslation = (key, {
  params,
  namespace,
  linePrefix,
  lineSuffix,
  targetLocale = false
}) => {
  const selectedLocale = (targetLocale === false) ? discoverCurrentLocale() : targetLocale
  let localizationValue = null
  forEach(clone(loadedLocaleBundles).reverse(), (localization) => {
    const localeBundleData = Object.values(localization)[0]
    if (localizationValue !== null) {
      return localizationValue
    }
    const localizationValueInBundle = localizationResolution(localeBundleData[selectedLocale], key, namespace)
    if (isUndefined(localizationValueInBundle)) {
      return
    }
    if (isNull(localizationValueInBundle) && !isNull(localizationValue)) {
      return
    }
    localizationValue = localizationValueInBundle
  })
  if (isEmpty(localizationValue) && targetLocale === false) {
    return getTranslation(key, {
      params: params,
      namespace: namespace,
      targetLocale: getLocaleFallback(),
      linePrefix,
      lineSuffix
    })
  }
  const finalLocalizationValue = (isEmpty(localizationValue)) ? key : localizationValue
  return parseLocaleValue(finalLocalizationValue, params, linePrefix, lineSuffix)
}

/**
 * Gets the fallback locale from config
 * @returns {*}
 */
const getLocaleFallback = () => {
  return dependencies.conf.get('localeFallback', moduleConfNamespace)
}

/**
 * Discovers what the current locale is
 * @returns {*}
 */
const discoverCurrentLocale = () => {
  const configuredStrategyName = dependencies.conf.get('localePickingStrategy.method', moduleConfNamespace)
  const strategy = get(localizationPickingStrategies, configuredStrategyName)
  const discoveredLocale = strategy(this)
  if (!includes(dependencies.conf.get('availableLocales', moduleConfNamespace), discoveredLocale)) {
    return getLocaleFallback()
  }
  return discoveredLocale
}

/**
 * Parses localization values
 * @param rawLocalizationValue {string[] | string}
 * @param params {string[]}
 * @param lineSuffix {string}
 * @param linePrefix {string}
 * @returns {string|false}
 */
const parseLocaleValue = (rawLocalizationValue, params, linePrefix, lineSuffix) => {
  // Fetch all nameless params in a separate array
  const namelessParams = filter(params, param => !isObject(param))
  // Gets the next nameless param from namelessParams
  const getNextNamelessParamValue = () => {
    if (typeof getNextNamelessParamValue.namelessParamIdx === 'undefined') {
      getNextNamelessParamValue.namelessParamIdx = -1
    }
    getNextNamelessParamValue.namelessParamIdx++
    return get(namelessParams, getNextNamelessParamValue.namelessParamIdx)
  }
  // Fetch all named params in a separate key value object and join them together (if multiple named params are found)
  /** @type {{[key: string]: any}} */
  const namedParams = reduce(
    filter(params, param => isObject(param)),
    (namedParamAcc, param) => {
      return { ...namedParamAcc, ...param }
    }
  )
  /*
  * Getter that takes in the full localization value part*,
  * extracts the name of the named pattern and attempts to return the provided value for that name
  * if localizationValuePart contained pattern is nameless returns false
  * if localizationValuePart contained pattern is named, returns the value from namedParams
  *   If there isn't such value in namedParams array, returns undefined. (Lo's default get behaviour)
  * *localization value part has to include zero or one occurrence of either named or nameless pattern
  *   See localization value split-age (positive lookahead) below of how it is achieved
  */
  /** @type {function(string): string|false|undefined} */
  const getNamedParamValue = (localizationValuePart) => {
    const pattern = last(localizationValuePart.match(/{{@\w*}}/g))
    const namedParamFromPattern = replace(pattern, /^{{@|}}$/gm, '')
    const namedParamValue = get(namedParams, namedParamFromPattern)
    if (isUndefined(namedParamValue) && eq(namedParamFromPattern, '')) {
      return false
    }
    return namedParamValue
  }
  /**
   * Returns false when localizationValuePart doesn't contain any patterns
   * @param localizationValuePart
   * @returns boolean
   */
  const doesntValuePartContainPattern = (localizationValuePart) => {
    return isEmpty(localizationValuePart.match(/{{@\w*}}/g))
  }
  /**
   * Multiline analysis of localization value
   */
  let localizationValue = rawLocalizationValue
  if (isArray(rawLocalizationValue)) {
    localizationValue = linePrefix + join(localizationValue, lineSuffix + linePrefix) + lineSuffix
  } else {
    localizationValue = linePrefix + localizationValue + lineSuffix
  }
  /**
   * Splits localization value to sections
   * that contain either 0 or 1 occurrences of patterns
   * For example given the string "My dog {{@dogName}} likes to play with {{@}}" will be split as following:
   * ["My dog ", "{{@dogName}} likes to play with ", "{{@}}"]
   * Positive lookahead, because we need to keep the patterns in the split,
   * as they'll be replaced with the matching value from the provided parameters
   * !!Or replaced with empty string if not found in provided parameters!!
   */
  const localizationValueSplit = split(localizationValue, /(?={{@\w*}})/g)
  const replacedLocalizationValueSplit = map(localizationValueSplit, (localizationValuePart) => {
    // Return parts without any pattern occurrences as is
    if (doesntValuePartContainPattern(localizationValuePart)) {
      return localizationValuePart
    }
    const namedParamValue = getNamedParamValue(localizationValuePart)
    // Replace named patterns, with empty string, if those patterns are nowhere to be found in provided params
    if (isUndefined(namedParamValue)) {
      return replace(localizationValuePart, /{{@\w*}}/g, '')
    }
    // Handling of nameless patterns
    if (namedParamValue === false) {
      const namelessParamValue = getNextNamelessParamValue()
      // Have we gone too far? Are there no more nameless patterns to fetch? replace with empty str if that's the case
      if (isUndefined(namelessParamValue)) {
        return replace(localizationValuePart, /{{@\w*}}/g, '')
      }
      return replace(localizationValuePart, /{{@\w*}}/g, namelessParamValue)
    }
    return replace(localizationValuePart, /{{@\w*}}/g, namedParamValue)
  })
  // Put the split localization Value Parts back together
  return join(replacedLocalizationValueSplit, '')
}

/**
 * List of strategies to be used in finding the current locale
 * @type {{discoverLocaleFromUrl: (function(): (boolean|*)), getLocaleFromConfig: (function({locale: *}): *)}}
 */
const localizationPickingStrategies = {
  discoverLocaleFromUrl: () => {
    const targetLevel = dependencies.conf.get('localePickingStrategy.targetDomainLevel', moduleConfNamespace)
    const locationHostChunked = window.location.host.split('.')
    const locationChunkLengthDiff = locationHostChunked.length - targetLevel
    if (locationChunkLengthDiff < 0) {
      return false
    }
    return get(locationHostChunked, locationChunkLengthDiff)
  },
  getLocaleFromConfig: () => {
    return dependencies.conf.get('localePickingStrategy.locale', moduleConfNamespace)
  }
}

/**
 * Given a list of localizations, it will attempt to retrieve a value
 * from a given key [and optionally a given namespace]
 * @param localization {object}
 * @param keySetting {string}
 * @param keyNamespace {string|null}
 * @returns {string|null}
 */
const localizationResolution = (localization, keySetting, keyNamespace = null) => {
  if (keyNamespace !== null) {
    if (has(localization, [keyNamespace, keySetting])) {
      return get(localization, [keyNamespace, keySetting])
    }
    return null
  }
  const foundLocaleSettings = find(localization, keySetting)
  if (typeof foundLocaleSettings === 'undefined') {
    return null
  }
  return foundLocaleSettings[keySetting]
}

/**
 * @type {{replaceLoadedBundle, replaceDependency, getCurrentLocale, translate, getLoadedBundles}}
 */
const LocaleService = {
  /**
   * Gets the locale item
   * @param key {string}
   * @param options {?object}
   * @returns {string|false}
   */
  translate: (key, options) => {
    const compiledOptions = defaults(options || {}, {
      namespace: dependencies.conf.get('defaultNamespaceLookup', moduleConfNamespace),
      params: [],
      linePrefix: '',
      lineSuffix: ''
    })
    return getTranslation(key, {
      params: compiledOptions.params,
      namespace: compiledOptions.namespace,
      linePrefix: compiledOptions.linePrefix,
      lineSuffix: compiledOptions.lineSuffix
    })
  },
  /**
   * Replaces the loaded bundle
   * @param bundleName {string}
   * @param payload {object}
   */
  replaceLoadedBundle: (bundleName, payload) => {
    const bundleIdx = findIndex(loadedLocaleBundles, (bundle) => get(keys(bundle), 0) === bundleName)
    loadedLocaleBundles[bundleIdx] = { [bundleName]: payload }
  },
  /**
   * Replaces a dependency
   * @param dependencyName {string}
   * @param payload {object}
   */
  replaceDependency: (dependencyName, payload) => {
    dependencies[dependencyName] = payload
  },
  /**
   * Gets the loaded bundles
   * @returns {object[]}
   */
  getLoadedBundles: () => {
    return loadedLocaleBundles
  },
  /**
   * Gets the current locale
   * @returns {string}
   */
  getCurrentLocale: () => {
    return discoverCurrentLocale()
  }
}

export default LocaleService
